import { useCallback, useEffect, useRef, useState } from 'react'
import {
  fetchPqrPreviewData,
  type FloorSheetPreviewData,
  type PqrPreviewTemplateData
} from '../../../utils/pqrPreviewClient'

/**
 * Result envelope returned by `usePqrPreviewData`.
 */
export interface UsePqrPreviewDataResult {
  /** Last successful payload from the server, or `null` while loading. */
  data: PqrPreviewTemplateData | null
  /** Floor Sheet page(s) accompanying the PQR PDF. */
  floorSheets: FloorSheetPreviewData[]
  /** `wps_sections.version` round-tripped from the server. `0` until first load. */
  version: number
  /** `true` only on the very first load before any payload exists. */
  isLoading: boolean
  /** Most recent error message, or `null` when the last fetch succeeded. */
  error: string | null
  /** Re-runs the fetch and updates `data` / `version`. */
  refetch: () => Promise<void>
}

/**
 * Hook owning the on-page preview snapshot for the PQR Preview canvas.
 *
 * The PQR Preview page edits sections through modals which produce sparse
 * patches; once a patch is saved (POST `/generate-pqr`) the server's
 * aggregation needs to be re-run for the canvas to stay in sync (welder
 * lists, equipment lists, comments, accompanying reports all depend on
 * aggregation that is too heavy to mirror client-side).
 *
 * Callers invoke `refetch()` after every successful save to bring the
 * canvas back to perfect parity with what the PDF will render.
 *
 * `weldScope` (optional) scopes the fetched payload to one or many welds;
 * changes are part of the refetch dep list so switching weld selections
 * reloads the canvas. Pass `null` / `undefined` / empty array for legacy
 * aggregated view.
 */
export function usePqrPreviewData(
  projectId: string | undefined,
  pipeSetId: string | undefined,
  weldScope?: string | string[] | null
): UsePqrPreviewDataResult {
  const [data, setData] = useState<PqrPreviewTemplateData | null>(null)
  const [floorSheets, setFloorSheets] = useState<FloorSheetPreviewData[]>([])
  const [version, setVersion] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Guard against state updates after unmount (e.g. user navigates away
  // mid-fetch). A `useRef` flag keeps the dep list of `refetch` empty so
  // callers can use it as a stable callback.
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const refetch = useCallback(async (): Promise<void> => {
    if (!projectId || !pipeSetId) return
    try {
      const result = await fetchPqrPreviewData(projectId, pipeSetId, weldScope ?? null)
      if (!mounted.current) return
      setData(result.data)
      setFloorSheets(result.floorSheets ?? [])
      setVersion(result.version)
      setError(null)
    } catch (err: any) {
      if (!mounted.current) return
      setError(err?.message || 'Failed to load PQR preview')
    } finally {
      if (mounted.current) setIsLoading(false)
    }
  }, [projectId, pipeSetId, weldScope])

  useEffect(() => {
    setIsLoading(true)
    void refetch()
  }, [refetch])

  return { data, floorSheets, version, isLoading, error, refetch }
}
